import React, { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { Typography, Tooltip } from "@mui/joy";
import { AddRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import { LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { ServiceReminderList } from "../../graphql/queries/serviceReminderList";
import { DefaultServiceTaskOptions } from "../../graphql/queries/defaultServiceTaskList";
import { sortByNextDueDate } from "../../utils/serviceReminderStatus";
import ServiceReminderCard, { ServiceReminderLike } from "../ServiceReminderCard";
import ServiceReminderEmptyIllustration from "../ServiceReminderEmptyIllustration";
import CreateServiceReminderModal from "../ServiceReminderModals/CreateServiceReminderModal";
import UpdateServiceReminderModal from "../ServiceReminderModals/UpdateServiceReminderModal";
import ResetServiceReminderModal from "../ServiceReminderModals/ResetServiceReminderModal";
import DeleteServiceReminderModal from "../ServiceReminderModals/DeleteServiceReminderModal";

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
}

const AssetServiceReminders = ({ installedBaseNodeId }: Props) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [createOpen, setCreateOpen] = useState(false);
    const [editReminder, setEditReminder] = useState<ServiceReminderLike | null>(null);
    const [resetReminder, setResetReminder] = useState<ServiceReminderLike | null>(null);
    const [deleteReminder, setDeleteReminder] = useState<ServiceReminderLike | null>(null);

    const { data, loading } = useQuery(ServiceReminderList, {
        variables: { installedBaseNodeId },
        skip: !installedBaseNodeId,
        fetchPolicy: "cache-and-network",
    });

    // Full catalog of default service tasks — used to offer only the types that
    // don't yet have a reminder (duplicates aren't allowed).
    const { data: taskData } = useQuery(DefaultServiceTaskOptions, {
        variables: { pagination: { start: 0, limit: 100 } },
        fetchPolicy: "cache-and-network",
    });

    const reminders: ServiceReminderLike[] = useMemo(
        () => sortByNextDueDate(_.get(data, "serviceReminderList.serviceReminders", []) ?? []),
        [data]
    );

    const availableServiceTasks: { id: string; name: string }[] = useMemo(() => {
        const allTasks = _.get(taskData, "defaultServiceTaskList.defaultServiceTasks", []) ?? [];
        const usedTaskIds = reminders.map((r) => r.defaultServiceTask?.id).filter(Boolean);
        return allTasks
            .filter((task: any) => task?.id && !usedTaskIds.includes(task.id))
            .map((task: any) => ({ id: task.id, name: task.name }));
    }, [taskData, reminders]);

    const noTypesAvailable = availableServiceTasks.length === 0;

    // Deep-link handling: ?reset=<id> auto-opens the reset dialog, ?focus=<id> highlights a card.
    const resetParam = searchParams.get("reset");
    const focusParam = searchParams.get("focus");

    const clearParam = (key: string) => {
        const next = new URLSearchParams(searchParams);
        next.delete(key);
        setSearchParams(next, { replace: true });
    };

    // Open the reset dialog once for a deep-linked reminder.
    const resetTarget = resetReminder ?? (resetParam ? reminders.find((r) => r.id === resetParam) : undefined);
    React.useEffect(() => {
        if (resetParam && !resetReminder) {
            const target = reminders.find((r) => r.id === resetParam);
            if (target) setResetReminder(target);
        }
    }, [resetParam, resetReminder, reminders]);

    const serviceName = (r?: ServiceReminderLike | null) =>
        r?.defaultServiceTask?.name || r?.serviceTaskTypeKey;

    return (
        <Box sx={{ width: "100%" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: "8px" }}>
                <Typography level="titleLarge">{t("Service Reminders")}</Typography>
                <Tooltip title={noTypesAvailable ? t("All service types already have a reminder.") : ""}>
                    <span>
                        <Button
                            variant="contained"
                            startIcon={<AddRounded />}
                            onClick={() => setCreateOpen(true)}
                            disabled={noTypesAvailable}
                            sx={{
                                color: "#333333",
                                backgroundColor: "#FDDDCD",
                                "&:hover": {
                                    backgroundColor: "#FFBA99",
                                },
                            }}
                        >
                            {t("Create")}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {loading && reminders.length === 0 ? (
                <LoadingScreen />
            ) : reminders.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "48px" }}>
                    <ServiceReminderEmptyIllustration width={200} />
                    <Typography level="body-md" sx={{ pt: "16px", textAlign: "center", color: "text.secondary" }}>
                        {t("No service reminders yet. Create one to start tracking service intervals.")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {reminders.map((reminder) => (
                        <ServiceReminderCard
                            key={reminder.id}
                            reminder={reminder}
                            highlighted={focusParam === reminder.id}
                            onReset={() => setResetReminder(reminder)}
                            onEdit={() => setEditReminder(reminder)}
                            onDelete={() => setDeleteReminder(reminder)}
                        />
                    ))}
                </Box>
            )}

            {createOpen && (
                <CreateServiceReminderModal
                    open={createOpen}
                    installedBaseNodeId={installedBaseNodeId}
                    availableServiceTasks={availableServiceTasks}
                    onClose={() => setCreateOpen(false)}
                />
            )}

            {editReminder && (
                <UpdateServiceReminderModal
                    open={Boolean(editReminder)}
                    reminder={editReminder}
                    serviceName={serviceName(editReminder)}
                    onClose={() => setEditReminder(null)}
                />
            )}

            {resetTarget && (
                <ResetServiceReminderModal
                    open={Boolean(resetTarget)}
                    installedBaseNodeId={installedBaseNodeId}
                    reminderId={resetTarget.id}
                    serviceName={serviceName(resetTarget)}
                    onClose={() => {
                        setResetReminder(null);
                        if (resetParam) clearParam("reset");
                    }}
                />
            )}

            {deleteReminder && (
                <DeleteServiceReminderModal
                    open={Boolean(deleteReminder)}
                    reminderId={deleteReminder.id}
                    serviceName={serviceName(deleteReminder)}
                    onClose={() => setDeleteReminder(null)}
                />
            )}
        </Box>
    );
};

export default AssetServiceReminders;
